import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  closeModal,
  updateLinkedAccounts,
  syncAndDownload,
} from 'loot-core/client/actions';

import { useAccounts } from '../../hooks/useAccounts';
import { useActions } from '../../hooks/useActions';
import { SvgExclamationOutline } from '../../icons/v1';
import { reauthorizeBank } from '../../quiltt';
import { theme } from '../../style';
import { Button } from '../common/Button';
import { Link } from '../common/Link';
import { Popover } from '../common/Popover';
import { View } from '../common/View';
import { useAuth } from '../../hooks/useAuth';

type AccountSyncCheckProps = {
  renderButton?: (
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    triggerRef,
  ) => JSX.Element;
  accountId?: string;
};

function getErrorMessage(type, code) {
  switch (type?.toUpperCase()) {
    case 'ITEM_ERROR':
      switch (code?.toUpperCase()) {
        case 'NO_ACCOUNTS':
          return 'No open accounts could be found. Did you close the account? If so, unlink the account.';
        case 'ITEM_LOGIN_REQUIRED':
          return 'Your password or something else has changed with your bank and you need to login again.';
        default:
      }
      break;

    case 'INVALID_INPUT':
      switch (code?.toUpperCase()) {
        case 'INVALID_ACCESS_TOKEN':
          return 'Item is no longer authorized. You need to login again.';
        default:
      }
      break;

    case 'RATE_LIMIT_EXCEEDED':
      return 'Rate limit exceeded for this item. Please try again later.';

    case 'BILLING_INACTIVE':
      return (
        <>
          Your subscription plan has expired. Go to {' '}
          <Link variant="internal" to="/billing">
            billing
          </Link>{' '}
          to active your subscription again.
        </>
      );

    // quiltt
    case 'QUILTT_ERROR':
      switch (code?.toUpperCase()) {
        case 'ERROR':
          return (
            <>
              This connection cannot be repaired right now. Get{' '}
              <Link variant="external" to="mailto:support@goodsteward.io">
                in touch
              </Link>{' '}
              with us for support.
            </>
          );
        case 'ERROR_REPAIRABLE':
          return 'Your password or something else has changed with your bank and you need to login again.';
        default:
      }
      break;

    default:
  }

  return (
    <>
      An internal error occurred. Try to login again, or get{' '}
      <Link variant="external" to="mailto:support@goodsteward.io">
        in touch
      </Link>{' '}
      for support.
    </>
  );
}

export function AccountSyncCheck({
  renderButton,
  accountId,
}: AccountSyncCheckProps) {
  const accounts = useAccounts();
  const dispatch = useDispatch();
  const { refreshAuthState } = useAuth();
  const failedAccounts = useSelector(state => state.account.failedAccounts);
  const { unlinkAccount, pushModal } = useActions();

  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  if (!failedAccounts) {
    return null;
  }

  const error = failedAccounts.get(accountId || id);
  if (!error) {
    return null;
  }

  const account = accounts.find(account => account.id === (accountId || id));
  const { type, code } = error;
  const showAuth =
    type === 'QUILTT_ERROR' && code === 'ERROR_REPAIRABLE' ||
    // old checks (gocardless related)
    (type === 'ITEM_ERROR' && code === 'ITEM_LOGIN_REQUIRED') ||
    (type === 'INVALID_INPUT' && code === 'INVALID_ACCESS_TOKEN');

  function reauth() {
    setOpen(false);

    reauthorizeBank(pushModal, account.account_id, async () => {
      dispatch(updateLinkedAccounts());
      dispatch(syncAndDownload(account.id));
      dispatch(closeModal());
    });
  }

  async function unlink() {
    await unlinkAccount(account.id);
    await refreshAuthState();
    setOpen(false);
  }

  return (
    <View>
      {renderButton ? (
        renderButton(setOpen, triggerRef)
      ) : (
        <Button
          ref={triggerRef}
          type="bare"
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            color: theme.errorText,
            backgroundColor: theme.errorBackground,
            padding: '4px 8px',
            borderRadius: 4,
          }}
          onClick={() => setOpen(true)}
        >
          <SvgExclamationOutline
            style={{ width: 14, height: 14, marginRight: 5 }}
          />{' '}
          This account is experiencing connection problems. Let’s fix it.
        </Button>
      )}

      <Popover
        triggerRef={triggerRef}
        placement="bottom start"
        isOpen={open}
        onOpenChange={() => setOpen(false)}
        style={{ fontSize: 14, padding: 15, maxWidth: 400 }}
      >
        <div style={{ marginBottom: '1.15em' }}>
          The following reason prevents from syncing with your financial institution:
        </div>

        <div style={{ marginBottom: '1.25em', color: theme.errorText }}>
          {getErrorMessage(error.type, error.code)}
        </div>

        <View style={{ justifyContent: 'flex-end', flexDirection: 'row' }}>
          {showAuth ? (
            <>
              <Button onClick={unlink}>Unlink</Button>
              <Button type="primary" onClick={reauth} style={{ marginLeft: 5 }}>
                Reauthorize
              </Button>
            </>
          ) : (
            <Button onClick={unlink}>Unlink account</Button>
          )}
        </View>
      </Popover>
    </View>
  );
}
